import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Lock, Eye, MessageSquare, Send, CheckCircle, AlertCircle, FileText, Building, Calendar, DollarSign } from 'lucide-react'
import api from '../lib/api'
import toast from 'react-hot-toast'

const SharedProposal = () => {
  const { token } = useParams()
  const [password, setPassword] = useState('')
  const [isVerified, setIsVerified] = useState(false)
  const [loading, setLoading] = useState(false)
  const [proposal, setProposal] = useState(null)
  const [error, setError] = useState('')
  
  // Comment form state
  const [commentForm, setCommentForm] = useState({
    reviewerName: '',
    reviewerEmail: '',
    sectionTitle: 'General',
    comment: ''
  })
  const [submittingComment, setSubmittingComment] = useState(false)

  const handleVerify = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await api.post(`/proposals/shared/${token}/verify`, { password })
      setProposal(response.data.proposal)
      setIsVerified(true)
      toast.success('Access granted!')
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid password or link expired')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!commentForm.reviewerName || !commentForm.comment) {
      toast.error('Please enter your name and comment')
      return
    }

    setSubmittingComment(true)
    try {
      const response = await api.post(`/proposals/shared/${token}/comment`, commentForm)
      toast.success('Comment submitted successfully!')
      
      // Add comment to local state
      setProposal(prev => ({
        ...prev,
        externalComments: [...(prev.externalComments || []), response.data.comment]
      }))
      
      // Reset form
      setCommentForm(prev => ({ ...prev, comment: '', sectionTitle: 'General' }))
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit comment')
    } finally {
      setSubmittingComment(false)
    }
  }

  // Password entry screen
  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Protected Proposal
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Enter the password to view this proposal
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Access Proposal'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
            This link was shared with you for review purposes.
          </p>
        </div>
      </div>
    )
  }

  // Proposal view
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {proposal?.title || 'Proposal'}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Shared for review
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Eye className="w-4 h-4" />
              <span>View Only</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Company Info */}
        {proposal?.companyInfo?.name && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-center gap-4">
              {proposal.companyInfo.logo && (
                <img src={proposal.companyInfo.logo} alt="Company Logo" className="w-16 h-16 object-contain" />
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {proposal.companyInfo.name}
                </h2>
                {proposal.companyInfo.website && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{proposal.companyInfo.website}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cover Page */}
        {proposal?.coverPage && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 mb-6 text-white">
            <h1 className="text-3xl font-bold mb-2">{proposal.coverPage.title || proposal.title}</h1>
            {proposal.coverPage.subtitle && (
              <p className="text-xl opacity-90 mb-4">{proposal.coverPage.subtitle}</p>
            )}
            <div className="flex items-center gap-6 text-sm opacity-80">
              {proposal.coverPage.date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(proposal.coverPage.date).toLocaleDateString()}
                </span>
              )}
              {proposal.coverPage.preparedBy && (
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" />
                  {proposal.coverPage.preparedBy}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Executive Summary */}
        {proposal?.executiveSummary && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Executive Summary</h2>
            <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
              {proposal.executiveSummary}
            </div>
          </div>
        )}

        {/* Sections */}
        {proposal?.sections?.map((section, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {index + 1}. {section.title}
            </h2>
            <div 
              className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 proposal-content"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          </div>
        ))}

        {/* Budget */}
        {proposal?.budget?.amount && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Investment
            </h2>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {proposal.budget.currency || 'INR'} {proposal.budget.amount.toLocaleString()}
            </p>
          </div>
        )}

        {/* Timeline */}
        {proposal?.timeline && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Timeline</h2>
            <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
              {proposal.timeline}
            </div>
          </div>
        )}

        {/* Comments Section */}
        {proposal?.allowComments && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Review Comments
            </h2>

            {/* Existing Comments */}
            {proposal.externalComments?.length > 0 && (
              <div className="space-y-4 mb-6">
                {proposal.externalComments.map((comment, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {comment.reviewerName}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(comment.timestamp).toLocaleString()}
                      </span>
                    </div>
                    {comment.sectionTitle !== 'General' && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded mb-2 inline-block">
                        {comment.sectionTitle}
                      </span>
                    )}
                    <p className="text-gray-700 dark:text-gray-300">{comment.comment}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <form onSubmit={handleSubmitComment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={commentForm.reviewerName}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, reviewerName: e.target.value }))}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    value={commentForm.reviewerEmail}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, reviewerEmail: e.target.value }))}
                    placeholder="john@example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Section
                </label>
                <select
                  value={commentForm.sectionTitle}
                  onChange={(e) => setCommentForm(prev => ({ ...prev, sectionTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="General">General Feedback</option>
                  {proposal.sections?.map((section, index) => (
                    <option key={index} value={section.title}>{section.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Your Comment *
                </label>
                <textarea
                  value={commentForm.comment}
                  onChange={(e) => setCommentForm(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Share your feedback or suggestions..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={submittingComment}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {submittingComment ? 'Submitting...' : 'Submit Comment'}
              </button>
            </form>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-6">
        <div className="max-w-5xl mx-auto px-4 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>This proposal was shared with you for review purposes only.</p>
          <p className="mt-1">Please do not share this link or password with others.</p>
        </div>
      </footer>
    </div>
  )
}

export default SharedProposal
